import React from 'react';

import SideMenu from 'components/SideMenu';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import Header from 'components/Header';
import useAsyncFetch from 'hooks/async-fetch';
import { useProjectsDispatch, useVMThunkDispatch } from 'hooks';
import { vmActions } from 'store/virtual-machine';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { UserRole } from 'store/user/types/state.types';
import NotificationContainer from 'components/NotificationContainer';
import { projectActions } from 'store/project';
import { ServicePermissions } from 'components/Project/types/enums';
import { usePermission } from 'hooks/use-permission';
import useSessionIdle from 'hooks/use-session-idle';
import TwoFAEnforcementModal from 'components/twoFA/TwoFAEnforcementModal/TwoFAEnforcementModal';
import styles from './AppLayout.module.scss';

const Layout = () => {
  const user = useSelector((state: RootState) => state.user);
  const location = useLocation();
  const projectsDispatch = useProjectsDispatch();
  const { hasAnyPermission } = usePermission();
  const thunkVMDispatch = useVMThunkDispatch();

  useSessionIdle();

  const [fetchResources] = useAsyncFetch({
    asyncFunc: async () => {
      await Promise.all([
        thunkVMDispatch(vmActions.fetchParamLimits()),
        thunkVMDispatch(vmActions.fetchDiskSpeed()),
      ]);
    },
  });

  React.useEffect(() => {
    if (
      hasAnyPermission([
        ServicePermissions.VIRTUAL_SERVERS,
        ServicePermissions.NETWORK,
        ServicePermissions.BACKUPS,
        ServicePermissions.CUSTOM_IMAGES,
        ServicePermissions.DISKS,
        ServicePermissions.LOAD_BALANCERS,
        ServicePermissions.KUBERNETES,
        ServicePermissions.S3,
        ServicePermissions.MANAGED_DATABASES,
        ServicePermissions.ROUTERS,
      ]) && !user.twoFaEnforcement?.blocking
    ) {
      let timer: number | undefined;
      if (user.role === UserRole.Customer) {
        fetchResources();
        timer = window.setInterval(fetchResources, 20000);
      }
      return () => clearInterval(timer);
    }
    return () => null;
  }, [user]);

  React.useEffect(() => {
    if (user.twoFaEnforcement?.blocking) {
      return;
    }
    projectsDispatch(projectActions.fetchProjectList);
    if (user.isRootUser) {
      projectsDispatch(projectActions.fetchUsers);
    }
  }, []);

  const isTwoFaPage = location.pathname === '/settings/two-fa';
  if (user?.twoFaEnforcement?.blocking && !isTwoFaPage) {
    return <Navigate to="/settings/two-fa" replace />;
  }

  return (
    <div className={styles.mainLayout}>
      <SideMenu />
      <Header />
      <main className={styles.innerPage}>
        {/* При смене региона страницы монтируются заново и перезапрашивают списки */}
        <Outlet key={user.region?.id} />
      </main>
      <TwoFAEnforcementModal />
      <NotificationContainer />
    </div>
  );
};
export default Layout;
